import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { Project, SceneWithShots, Shot, ProjectConfig } from '../types/database';

interface ProjectState {
  // Current project
  currentProject: Project | null;
  projectConfig: ProjectConfig | null;
  scenes: SceneWithShots[];

  // Loading states
  isLoading: boolean;
  error: string | null;

  // Actions
  setCurrentProject: (project: Project | null) => void;
  setProjectConfig: (config: ProjectConfig | null) => void;
  setScenes: (scenes: SceneWithShots[]) => void;
  updateScene: (sceneId: string, updates: Partial<SceneWithShots>) => void;
  updateShot: (sceneId: string, shotId: string, updates: Partial<SceneWithShots['shots'][0]>) => void;
  saveShot: (sceneId: string, shotId: string, updates: Partial<Shot>) => Promise<void>;
  addScene: (scene: SceneWithShots) => void;
  addShot: (sceneId: string, shot: SceneWithShots['shots'][0]) => void;
  removeShot: (sceneId: string, shotId: string) => void;
  removeScene: (sceneId: string) => void;
  reorderScenes: (sceneIds: string[]) => void;
  reorderShots: (sceneId: string, shotIds: string[]) => void;

  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Computed
  getTotalDuration: () => number;
  getSceneCount: () => number;
  getShotCount: () => number;

  // Reset
  resetProject: () => void;
}

const initialState = {
  currentProject: null,
  projectConfig: null,
  scenes: [],
  isLoading: false,
  error: null,
};

export const useProjectStore = create<ProjectState>((set, get) => ({
  ...initialState,

  setCurrentProject: (project) => set({ currentProject: project }),
  setProjectConfig: (config) => set({ projectConfig: config }),
  setScenes: (scenes) => set({ scenes }),

  updateScene: (sceneId, updates) => {
    set((state) => ({
      scenes: state.scenes.map((scene) =>
        scene.id === sceneId ? { ...scene, ...updates } : scene
      ),
    }));
  },

  updateShot: (sceneId, shotId, updates) => {
    set((state) => ({
      scenes: state.scenes.map((scene) =>
        scene.id === sceneId
          ? {
              ...scene,
              shots: scene.shots.map((shot) =>
                shot.id === shotId ? { ...shot, ...updates } : shot
              ),
            }
          : scene
      ),
    }));
  },

  saveShot: async (sceneId, shotId, updates) => {
    // First update local state for immediate feedback
    set((state) => ({
      scenes: state.scenes.map((scene) =>
        scene.id === sceneId
          ? {
              ...scene,
              shots: scene.shots.map((shot) =>
                shot.id === shotId ? { ...shot, ...updates } : shot
              ),
            }
          : scene
      ),
    }));

    // Then save to database
    try {
      const { error } = await supabase
        .from('shots')
        .update(updates)
        .eq('id', shotId);

      if (error) {
        console.error('Failed to save shot:', error);
        // Optionally revert local state on error
      }
    } catch (error) {
      console.error('Save shot error:', error);
    }
  },

  addScene: (scene) => {
    set((state) => ({
      scenes: [...state.scenes, scene],
    }));
  },

  addShot: (sceneId, shot) => {
    set((state) => ({
      scenes: state.scenes.map((scene) =>
        scene.id === sceneId
          ? { ...scene, shots: [...scene.shots, shot] }
          : scene
      ),
    }));
  },

  removeShot: (sceneId, shotId) => {
    set((state) => ({
      scenes: state.scenes.map((scene) =>
        scene.id === sceneId
          ? { ...scene, shots: scene.shots.filter((s) => s.id !== shotId) }
          : scene
      ),
    }));
  },

  removeScene: (sceneId) => {
    set((state) => ({
      scenes: state.scenes.filter((s) => s.id !== sceneId),
    }));
  },

  reorderScenes: (sceneIds) => {
    set((state) => {
      const sceneMap = new Map(state.scenes.map((s) => [s.id, s]));
      return {
        scenes: sceneIds
          .map((id) => sceneMap.get(id))
          .filter((s): s is SceneWithShots => !!s)
          .map((scene, index) => ({ ...scene, order_index: index })),
      };
    });
  },

  reorderShots: (sceneId, shotIds) => {
    // Update local state immediately
    set((state) => ({
      scenes: state.scenes.map((scene) => {
        if (scene.id !== sceneId) return scene;

        const shotMap = new Map(scene.shots.map((s) => [s.id, s]));
        return {
          ...scene,
          shots: shotIds
            .map((id) => shotMap.get(id))
            .filter((s): s is SceneWithShots['shots'][0] => !!s)
            .map((shot, index) => ({ ...shot, order_index: index })),
        };
      }),
    }));

    // Persist to database - use individual updates to avoid RLS issues
    Promise.all(
      shotIds.map((id, index) =>
        supabase
          .from('shots')
          .update({ order_index: index })
          .eq('id', id)
      )
    ).then((results) => {
      const failed = results.find((r) => r.error);
      if (failed?.error) {
        console.error('Failed to persist shot reorder:', failed.error);
      }
    });
  },

  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  getTotalDuration: () => {
    const { scenes } = get();
    return scenes.reduce(
      (acc, scene) =>
        acc + scene.shots.reduce((sAcc, shot) => sAcc + (shot.duration_sec || 0), 0),
      0
    );
  },

  getSceneCount: () => {
    return get().scenes.length;
  },

  getShotCount: () => {
    return get().scenes.reduce((acc, scene) => acc + scene.shots.length, 0);
  },

  resetProject: () => set(initialState),
}));
